import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;


/* =========================================================
   FLICKON — FACTORY SETUP CONFIRMATION
   ========================================================= */

const FLICKON_SETUP_DRAWER_ID = 'flickon-setup-confirm-drawer';

/**
 * Normalizes option text for comparison.
 * @param {*} value
 * @returns {string}
 */
function flickonNormalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

/**
 * Finds the main variant picker for a product.
 *
 * @param {string | number | undefined} productId
 * @returns {HTMLElement | null}
 */
function flickonGetVariantPicker(productId) {
  if (!productId) return null;

  const targetId = String(productId);

  const pickers = document.querySelectorAll(
    'variant-picker[data-product-id]'
  );

  for (const picker of pickers) {
    if (
      picker instanceof HTMLElement &&
      picker.dataset.productId === targetId
    ) {
      return picker;
    }
  }

  return null;
}

/**
 * Checks if the product actually uses the FlickOn
 * Factory Ambient Lighting Setup option.
 *
 * Products without this option must behave exactly
 * like normal Horizon products.
 *
 * @param {string | number | undefined} productId
 * @returns {boolean}
 */
function flickonProductUsesAmbientSetup(productId) {
  const picker = flickonGetVariantPicker(productId);

  if (!picker) return false;

  return Boolean(
    picker.querySelector(
      '[data-flickon-ambient-setup="true"]'
    )
  );
}

/**
 * Returns the current FlickOn logical setup state.
 *
 * Shopify can visually have Ice-Blue selected while
 * FlickOn still considers the configuration UNKNOWN.
 *
 * @param {string | number | undefined} productId
 * @returns {{confirmed: boolean, value: string}}
 */
function flickonGetSetupState(productId) {
  const id = String(productId || '');

  if (!id) {
    return {
      confirmed: false,
      value: 'unknown',
    };
  }

  const states =
    window.__flickonProductSetupStates || {};

  const state = states[id];

  if (!state) {
    return {
      confirmed: false,
      value: 'unknown',
    };
  }

  return {
    confirmed: state.confirmed === true,
    value: state.value || 'unknown',
  };
}

/**
 * Determines if Add to Cart must be stopped
 * until the customer confirms their factory setup.
 *
 * @param {string | number | undefined} productId
 * @returns {boolean}
 */
function flickonNeedsSetupConfirmation(productId) {
  if (!flickonProductUsesAmbientSetup(productId)) {
    return false;
  }

  const state = flickonGetSetupState(productId);

  return state.confirmed !== true;
}

/**
 * Restores body scrolling after the confirmation drawer.
 */
function flickonRestoreDrawerScroll() {
  const drawer =
    document.getElementById(
      FLICKON_SETUP_DRAWER_ID
    );

  if (!drawer) return;

  const previousHtmlOverflow =
    drawer.dataset.previousHtmlOverflow || '';

  const previousBodyOverflow =
    drawer.dataset.previousBodyOverflow || '';

  document.documentElement.style.overflow =
    previousHtmlOverflow;

  document.body.style.overflow =
    previousBodyOverflow;

  delete drawer.dataset.previousHtmlOverflow;
  delete drawer.dataset.previousBodyOverflow;
}

/**
 * Closes the Factory Setup confirmation drawer.
 */
function flickonCloseSetupDrawer() {
  const drawer =
    document.getElementById(
      FLICKON_SETUP_DRAWER_ID
    );

  if (!drawer) return;

  drawer.classList.remove('is-open');

  drawer.setAttribute(
    'aria-hidden',
    'true'
  );

  flickonRestoreDrawerScroll();
}

/**
 * Applies a setup through the EXISTING Horizon picker.
 *
 * This is important:
 * we don't manually manipulate variant IDs.
 * We trigger the existing picker instead so Horizon
 * remains responsible for variant, price, media and URL.
 *
 * @param {string} productId
 * @param {'ice_blue'|'multicolor'} setup
 */
function flickonSelectSetup(productId, setup) {
  const picker =
    flickonGetVariantPicker(productId);

  if (!picker) return;

  const radios =
    picker.querySelectorAll(
      'input[type="radio"][data-flickon-ambient-setup="true"]'
    );

  for (const radio of radios) {
    if (!(radio instanceof HTMLInputElement)) {
      continue;
    }

    const value =
      flickonNormalize(radio.value);

    const isTarget =
      setup === 'multicolor'
        ? value.includes('multicolor')
        : (
            value.includes('ice-blue') ||
            value.includes('ice blue')
          );

    if (!isTarget) continue;

    /*
      .click() is intentional.

      It allows the existing FlickOn logic in
      variant-main-picker.liquid to register
      the deliberate customer choice.

      It also lets Horizon handle the actual variant.
    */

    radio.click();

    flickonCloseSetupDrawer();

    return;
  }

  /*
    Dropdown fallback in case the presentation
    is changed in Theme Editor later.
  */

  const select =
    picker.querySelector(
      'select[data-flickon-ambient-setup="true"]'
    );

  if (!(select instanceof HTMLSelectElement)) {
    return;
  }

  let targetValue = null;

  for (const option of select.options) {
    const value =
      flickonNormalize(option.value);

    const isTarget =
      setup === 'multicolor'
        ? value.includes('multicolor')
        : (
            value.includes('ice-blue') ||
            value.includes('ice blue')
          );

    if (isTarget) {
      targetValue = option.value;
      break;
    }
  }

  if (!targetValue) return;

  select.value = targetValue;

  select.dispatchEvent(
    new Event(
      'change',
      {
        bubbles: true,
      }
    )
  );

  flickonCloseSetupDrawer();
}

/**
 * Creates the confirmation drawer once.
 *
 * @returns {HTMLElement}
 */
function flickonEnsureSetupDrawer() {
  const existing =
    document.getElementById(
      FLICKON_SETUP_DRAWER_ID
    );

  if (existing) return existing;

  const drawer =
    document.createElement('div');

  drawer.id =
    FLICKON_SETUP_DRAWER_ID;

  drawer.className =
    'flickon-setup-confirm-drawer';

  drawer.setAttribute(
    'aria-hidden',
    'true'
  );

  drawer.innerHTML = `
    <button
      type="button"
      class="flickon-setup-confirm-drawer__overlay"
      aria-label="Close"
      data-flickon-setup-close
    ></button>

    <section
      class="flickon-setup-confirm-drawer__panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flickon-setup-confirm-title"
    >
      <div
        class="flickon-setup-confirm-drawer__handle"
        aria-hidden="true"
      ></div>

      <div class="flickon-setup-confirm-drawer__header">

        <div>
          <div
            class="flickon-setup-confirm-drawer__eyebrow"
          >
            One detail before adding to cart
          </div>

          <h2
            id="flickon-setup-confirm-title"
            class="flickon-setup-confirm-drawer__title"
          >
            Confirm your factory setup
          </h2>
        </div>

        <button
          type="button"
          class="flickon-setup-confirm-drawer__close"
          aria-label="Close"
          data-flickon-setup-close
        >
          ×
        </button>

      </div>

      <p class="flickon-setup-confirm-drawer__copy">
        Select the ambient lighting setup originally fitted to your car.
      </p>

      <div class="flickon-setup-confirm-drawer__choices">

        <button
          type="button"
          class="flickon-setup-confirm-drawer__choice"
          data-flickon-setup-choice="ice_blue"
        >
          <span class="flickon-setup-confirm-drawer__choice-title">
            Ice-Blue
          </span>

          <span class="flickon-setup-confirm-drawer__choice-text">
            Fixed factory ambient lighting color
          </span>
        </button>

        <button
          type="button"
          class="flickon-setup-confirm-drawer__choice flickon-setup-confirm-drawer__choice--multicolor"
          data-flickon-setup-choice="multicolor"
        >
          <span class="flickon-setup-confirm-drawer__choice-title">
            Multicolor
          </span>

          <span class="flickon-setup-confirm-drawer__choice-text">
            Factory color selection available through SYNC
          </span>
        </button>

      </div>

      <p class="flickon-setup-confirm-drawer__footer">
        Not sure? You can close this window and check your factory configuration before ordering.
      </p>

    </section>
  `;

  document.body.appendChild(drawer);

  const style =
    document.createElement('style');

  style.textContent = `
    .flickon-setup-confirm-drawer {
      position: fixed;
      inset: 0;
      z-index: 100000;
      visibility: hidden;
      pointer-events: none;
    }

    .flickon-setup-confirm-drawer.is-open {
      visibility: visible;
      pointer-events: auto;
    }

    .flickon-setup-confirm-drawer__overlay {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      border: 0;
      background: rgba(0, 0, 0, 0.46);
      opacity: 0;
      cursor: pointer;
      transition: opacity 220ms ease;
    }

    .flickon-setup-confirm-drawer.is-open
    .flickon-setup-confirm-drawer__overlay {
      opacity: 1;
    }

    .flickon-setup-confirm-drawer__panel {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;

      width: 100%;
      max-height: 80dvh;

      box-sizing: border-box;

      padding:
        12px
        20px
        calc(24px + env(safe-area-inset-bottom));

      overflow-y: auto;
      overscroll-behavior: contain;

      background: #111111;
      color: #ffffff;

      border-radius: 24px 24px 0 0;

      transform: translateY(105%);

      transition:
        transform 280ms
        cubic-bezier(0.22, 0.61, 0.36, 1);
    }

    .flickon-setup-confirm-drawer.is-open
    .flickon-setup-confirm-drawer__panel {
      transform: translateY(0);
    }

    .flickon-setup-confirm-drawer__handle {
      width: 42px;
      height: 4px;

      margin: 0 auto 22px;

      border-radius: 999px;

      background: rgba(255,255,255,.35);
    }

    .flickon-setup-confirm-drawer__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
    }

    .flickon-setup-confirm-drawer__eyebrow {
      margin-bottom: 8px;

      color: #8d9598;

      font-size: 12px;
      line-height: 1.2;
      font-weight: 700;

      letter-spacing: .06em;

      text-transform: uppercase;
    }

    .flickon-setup-confirm-drawer__title {
      margin: 0;

      color: #ffffff;

      font-size: 27px;
      line-height: 1.08;
      font-weight: 600;

      letter-spacing: -.02em;
    }

    .flickon-setup-confirm-drawer__close {
      display: flex;
      align-items: center;
      justify-content: center;

      flex: 0 0 auto;

      width: 40px;
      height: 40px;

      padding: 0;

      border:
        1px solid
        rgba(255,255,255,.20);

      border-radius: 50%;

      background:
        rgba(255,255,255,.05);

      color: #ffffff;

      font-size: 25px;
      line-height: 1;

      cursor: pointer;
    }

    .flickon-setup-confirm-drawer__copy {
      margin:
        18px
        0
        20px;

      color: #a7adaf;

      font-size: 15px;
      line-height: 1.45;
    }

    .flickon-setup-confirm-drawer__choices {
      display: grid;
      grid-template-columns: 1fr;

      gap: 12px;
    }

    .flickon-setup-confirm-drawer__choice {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      width: 100%;

      padding: 20px;

      border:
        1px solid
        #3b4348;

      border-radius: 17px;

      background: #181d20;
      color: #ffffff;

      text-align: left;

      cursor: pointer;
    }

    .flickon-setup-confirm-drawer__choice--multicolor {
      border-color: #06c0c2;
    }

    .flickon-setup-confirm-drawer__choice-title {
      display: block;

      font-size: 19px;
      line-height: 1.2;
      font-weight: 700;
    }

    .flickon-setup-confirm-drawer__choice-text {
      display: block;

      margin-top: 7px;

      color: #9da4a7;

      font-size: 14px;
      line-height: 1.4;
      font-weight: 500;
    }

    .flickon-setup-confirm-drawer__footer {
      margin:
        18px
        0
        0;

      color: #777f82;

      font-size: 12px;
      line-height: 1.45;
    }

    @media screen and (min-width: 750px) {
      .flickon-setup-confirm-drawer__panel {
        left: 50%;
        right: auto;

        width: min(560px, calc(100% - 40px));

        transform:
          translate(-50%, 105%);

        border-radius:
          24px
          24px
          0
          0;
      }

      .flickon-setup-confirm-drawer.is-open
      .flickon-setup-confirm-drawer__panel {
        transform:
          translate(-50%, 0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .flickon-setup-confirm-drawer__overlay,
      .flickon-setup-confirm-drawer__panel {
        transition: none;
      }
    }
  `;

  document.head.appendChild(style);

  drawer.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const closeTrigger =
        event.target.closest(
          '[data-flickon-setup-close]'
        );

      if (closeTrigger) {
        flickonCloseSetupDrawer();
        return;
      }

      const choice =
        event.target.closest(
          '[data-flickon-setup-choice]'
        );

      if (!(choice instanceof HTMLElement)) {
        return;
      }

      const productId =
        drawer.dataset.productId;

      const setup =
        choice.dataset.flickonSetupChoice;

      if (
        !productId ||
        (
          setup !== 'ice_blue' &&
          setup !== 'multicolor'
        )
      ) {
        return;
      }

      flickonSelectSetup(
        productId,
        setup
      );
    }
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;

      if (
        !drawer.classList.contains(
          'is-open'
        )
      ) {
        return;
      }

      flickonCloseSetupDrawer();
    }
  );

  return drawer;
}

/**
 * Opens the Factory Setup confirmation drawer.
 *
 * @param {string | number | undefined} productId
 */
function flickonOpenSetupDrawer(productId) {
  if (!productId) return;

  const drawer =
    flickonEnsureSetupDrawer();

  drawer.dataset.productId =
    String(productId);

  if (
    !drawer.classList.contains(
      'is-open'
    )
  ) {
    drawer.dataset.previousHtmlOverflow =
      document.documentElement.style.overflow;

    drawer.dataset.previousBodyOverflow =
      document.body.style.overflow;
  }

  drawer.classList.add('is-open');

  drawer.setAttribute(
    'aria-hidden',
    'false'
  );

  document.documentElement.style.overflow =
    'hidden';

  document.body.style.overflow =
    'hidden';
}


/* =========================================================
   FLICKON — INSTALLATION SETUP CONFIRMATION
   Shown after factory setup is resolved, before /cart/add.
   ========================================================= */

const FLICKON_ATC_SETUP_DRAWER_ID =
  'flickon-atc-setup-drawer';

const FLICKON_ATC_SETUP_PROPERTY =
  'Installation setup';

const FLICKON_ATC_VERIFY_PROPERTY =
  '_verification_status';

const FLICKON_ATC_VERIFY_VALUE =
  'Verify before dispatch';

const FLICKON_ATC_NOT_SURE_PROPERTY_VALUE =
  'To be verified before dispatch';

/**
 * Maps the drawer choice to the storefront-visible
 * Installation setup line item property.
 *
 * @param {string} setup
 * @returns {string}
 */
function flickonGetAtcSetupPropertyValue(setup) {
  if (setup === 'Not sure') {
    return FLICKON_ATC_NOT_SURE_PROPERTY_VALUE;
  }

  return setup;
}

/**
 * @returns {Record<string, {
 *   setup: string,
 *   setupKind: string,
 *   verify: boolean
 * }>}
 */
function flickonGetAtcSetupStore() {
  if (!window.__flickonAtcSetupConfirmations) {
    window.__flickonAtcSetupConfirmations = {};
  }

  return window.__flickonAtcSetupConfirmations;
}

/**
 * @param {HTMLElement | null | undefined} productForm
 * @returns {boolean}
 */
function flickonProductUsesAtcSetupConfirmation(productForm) {
  return (
    productForm instanceof HTMLElement &&
    productForm.dataset.flickonAtcSetupConfirmation === 'true'
  );
}

/**
 * Resolves Ice Blue vs Multicolor from the live picker.
 *
 * @param {string | number | undefined} productId
 * @returns {'ice_blue' | 'multicolor'}
 */
function flickonGetCurrentSetupKind(productId) {
  const picker = flickonGetVariantPicker(productId);

  if (picker) {
    const checked = picker.querySelector(
      'input[type="radio"][data-flickon-ambient-setup="true"]:checked'
    );

    if (checked instanceof HTMLInputElement) {
      if (checked.dataset.flickonSetupValue === 'multicolor') {
        return 'multicolor';
      }

      if (checked.dataset.flickonSetupValue === 'ice_blue') {
        return 'ice_blue';
      }

      const value = flickonNormalize(checked.value);

      if (value.includes('multicolor')) {
        return 'multicolor';
      }

      if (
        value.includes('ice-blue') ||
        value.includes('ice blue')
      ) {
        return 'ice_blue';
      }
    }

    const select = picker.querySelector(
      'select[data-flickon-ambient-setup="true"]'
    );

    if (select instanceof HTMLSelectElement) {
      const selected = select.selectedOptions[0];
      const fromData =
        selected?.dataset.flickonSetupValue ||
        select.dataset.flickonSetupValue;

      if (fromData === 'multicolor') {
        return 'multicolor';
      }

      if (fromData === 'ice_blue') {
        return 'ice_blue';
      }

      const value = flickonNormalize(select.value);

      if (value.includes('multicolor')) {
        return 'multicolor';
      }
    }

    if (picker.dataset.flickonSetupValue === 'multicolor') {
      return 'multicolor';
    }

    if (picker.dataset.flickonSetupValue === 'ice_blue') {
      return 'ice_blue';
    }
  }

  const state = flickonGetSetupState(productId);

  if (state.value === 'multicolor') {
    return 'multicolor';
  }

  return 'ice_blue';
}

/**
 * Second option label/value depends on the current variant.
 *
 * @param {'ice_blue' | 'multicolor'} setupKind
 * @returns {string}
 */
function flickonGetAtcFlickOnOptionLabel(setupKind) {
  return setupKind === 'multicolor'
    ? 'FlickOn X11'
    : 'FlickOn';
}

/**
 * @param {HTMLElement | null | undefined} productForm
 * @returns {boolean}
 */
function flickonNeedsAtcSetupConfirmation(productForm) {
  if (!flickonProductUsesAtcSetupConfirmation(productForm)) {
    return false;
  }

  return productForm.dataset.flickonAtcSetupReady !== 'true';
}

/**
 * Marks the main ATC button so sticky does not animate yet.
 *
 * @param {Event | undefined} event
 * @param {HTMLElement | null | undefined} addToCartButton
 */
function flickonMarkAtcDeferred(event, addToCartButton) {
  const fromEvent =
    event?.target instanceof Element
      ? event.target.closest('[ref="addToCartButton"]')
      : null;

  const button =
    (fromEvent instanceof HTMLElement && fromEvent) ||
    (addToCartButton instanceof HTMLElement && addToCartButton) ||
    null;

  if (!button) return;

  button.dataset.flickonAtcDeferred = 'true';
}

/**
 * @param {HTMLFormElement} form
 * @param {string} propertyName
 * @param {string} marker
 * @returns {HTMLInputElement}
 */
function flickonGetOrCreatePropertyInput(form, propertyName, marker) {
  const expectedName = `properties[${propertyName}]`;

  let input = form.querySelector(
    `input[data-flickon-atc-property="${marker}"]`
  );

  if (input instanceof HTMLInputElement) {
    input.name = expectedName;
    return input;
  }

  for (const candidate of form.querySelectorAll('input[type="hidden"]')) {
    if (
      candidate instanceof HTMLInputElement &&
      candidate.name === expectedName
    ) {
      candidate.setAttribute('data-flickon-atc-property', marker);
      return candidate;
    }
  }

  input = document.createElement('input');
  input.type = 'hidden';
  input.name = expectedName;
  input.setAttribute('data-flickon-atc-property', marker);
  form.appendChild(input);

  return input;
}

/**
 * Writes the chosen setup into the live product form
 * immediately before FormData is built.
 *
 * @param {HTMLElement | null | undefined} productForm
 */
function flickonApplyAtcSetupProperties(productForm) {
  if (!flickonProductUsesAtcSetupConfirmation(productForm)) {
    return;
  }

  const form = productForm.querySelector('form');

  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const productId = String(productForm.dataset.productId || '');
  const state = flickonGetAtcSetupStore()[productId];

  if (!state?.setup) {
    return;
  }

  const setupInput = flickonGetOrCreatePropertyInput(
    form,
    FLICKON_ATC_SETUP_PROPERTY,
    'installation-setup'
  );

  setupInput.value = flickonGetAtcSetupPropertyValue(state.setup);

  const verifyInput = form.querySelector(
    'input[data-flickon-atc-property="verification-status"]'
  );

  if (state.setup === 'Not sure') {
    const input =
      verifyInput instanceof HTMLInputElement
        ? verifyInput
        : flickonGetOrCreatePropertyInput(
            form,
            FLICKON_ATC_VERIFY_PROPERTY,
            'verification-status'
          );

    input.value = FLICKON_ATC_VERIFY_VALUE;
    return;
  }

  if (verifyInput instanceof HTMLInputElement) {
    verifyInput.remove();
  }
}

/**
 * @param {HTMLElement | null | undefined} productForm
 * @returns {Record<string, string> | null}
 */
function flickonGetAtcSetupPropertyMap(productForm) {
  if (!flickonProductUsesAtcSetupConfirmation(productForm)) {
    return null;
  }

  const productId = String(productForm.dataset.productId || '');
  const state = flickonGetAtcSetupStore()[productId];

  if (!state?.setup) {
    return null;
  }

  /** @type {Record<string, string>} */
  const properties = {
    [FLICKON_ATC_SETUP_PROPERTY]:
      flickonGetAtcSetupPropertyValue(state.setup),
  };

  if (state.setup === 'Not sure') {
    properties[FLICKON_ATC_VERIFY_PROPERTY] =
      FLICKON_ATC_VERIFY_VALUE;
  }

  return properties;
}

/**
 * @param {HTMLElement | null | undefined} productForm
 */
function flickonClearAtcSetupReady(productForm) {
  if (!(productForm instanceof HTMLElement)) {
    return;
  }

  delete productForm.dataset.flickonAtcSetupReady;
}

function flickonRestoreAtcDrawerScroll() {
  const drawer = document.getElementById(
    FLICKON_ATC_SETUP_DRAWER_ID
  );

  if (!drawer) return;

  const previousHtmlOverflow =
    drawer.dataset.previousHtmlOverflow || '';

  const previousBodyOverflow =
    drawer.dataset.previousBodyOverflow || '';

  document.documentElement.style.overflow =
    previousHtmlOverflow;

  document.body.style.overflow =
    previousBodyOverflow;

  delete drawer.dataset.previousHtmlOverflow;
  delete drawer.dataset.previousBodyOverflow;
}

function flickonCloseAtcSetupDrawer() {
  const drawer = document.getElementById(
    FLICKON_ATC_SETUP_DRAWER_ID
  );

  if (!drawer) return;

  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  flickonRestoreAtcDrawerScroll();
}

/**
 * @param {HTMLElement} drawer
 * @param {string | number | undefined} productId
 */
function flickonSyncAtcSetupDrawerOptions(drawer, productId) {
  const setupKind = flickonGetCurrentSetupKind(productId);
  const secondLabel = flickonGetAtcFlickOnOptionLabel(setupKind);
  const secondOption = drawer.querySelector(
    '[data-flickon-atc-setup-option="flickon"]'
  );

  drawer.dataset.setupKind = setupKind;

  if (!(secondOption instanceof HTMLElement)) {
    return;
  }

  const input = secondOption.querySelector('input');
  const label = secondOption.querySelector(
    '[data-flickon-atc-setup-label]'
  );

  if (input instanceof HTMLInputElement) {
    input.value = secondLabel;
  }

  if (label) {
    label.textContent = secondLabel;
  }
}

/**
 * @param {HTMLElement} drawer
 */
function flickonResetAtcSetupDrawer(drawer) {
  const inputs = drawer.querySelectorAll(
    'input[name="flickon-atc-setup"]'
  );

  for (const input of inputs) {
    if (input instanceof HTMLInputElement) {
      input.checked = false;
    }
  }

  const hint = drawer.querySelector(
    '[data-flickon-atc-setup-hint]'
  );

  if (hint instanceof HTMLElement) {
    hint.hidden = true;
  }

  const confirm = drawer.querySelector(
    '[data-flickon-atc-setup-confirm]'
  );

  if (confirm instanceof HTMLButtonElement) {
    confirm.disabled = true;
  }
}

/**
 * @param {HTMLElement} drawer
 */
function flickonSyncAtcSetupDrawerSelection(drawer) {
  const selected = drawer.querySelector(
    'input[name="flickon-atc-setup"]:checked'
  );

  const hint = drawer.querySelector(
    '[data-flickon-atc-setup-hint]'
  );

  const confirm = drawer.querySelector(
    '[data-flickon-atc-setup-confirm]'
  );

  if (hint instanceof HTMLElement) {
    hint.hidden = !(
      selected instanceof HTMLInputElement &&
      selected.value === 'Not sure'
    );
  }

  if (confirm instanceof HTMLButtonElement) {
    confirm.disabled = !(selected instanceof HTMLInputElement);
  }

  for (const choice of drawer.querySelectorAll(
    '[data-flickon-atc-setup-option]'
  )) {
    if (!(choice instanceof HTMLElement)) continue;

    const input = choice.querySelector('input');
    choice.classList.toggle(
      'is-selected',
      input instanceof HTMLInputElement && input.checked
    );
  }
}

/**
 * @returns {HTMLElement}
 */
function flickonEnsureAtcSetupDrawer() {
  const existing = document.getElementById(
    FLICKON_ATC_SETUP_DRAWER_ID
  );

  if (existing) return existing;

  const drawer = document.createElement('div');

  drawer.id = FLICKON_ATC_SETUP_DRAWER_ID;
  drawer.className = 'flickon-atc-setup-drawer';
  drawer.setAttribute('aria-hidden', 'true');

  drawer.innerHTML = `
    <button
      type="button"
      class="flickon-atc-setup-drawer__overlay"
      aria-label="Close"
      data-flickon-atc-setup-close
    ></button>

    <section
      class="flickon-atc-setup-drawer__panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flickon-atc-setup-title"
    >
      <div
        class="flickon-atc-setup-drawer__handle"
        aria-hidden="true"
      ></div>

      <div class="flickon-atc-setup-drawer__header">
        <h2
          id="flickon-atc-setup-title"
          class="flickon-atc-setup-drawer__title"
        >
          One last detail
        </h2>

        <button
          type="button"
          class="flickon-atc-setup-drawer__close"
          aria-label="Close"
          data-flickon-atc-setup-close
        >
          ×
        </button>
      </div>

      <p class="flickon-atc-setup-drawer__copy">
        Which setup do you currently have?
      </p>

      <div
        class="flickon-atc-setup-drawer__choices"
        role="radiogroup"
        aria-labelledby="flickon-atc-setup-title"
      >
        <label
          class="flickon-atc-setup-drawer__choice"
          data-flickon-atc-setup-option="factory"
        >
          <input
            type="radio"
            name="flickon-atc-setup"
            value="Factory Ford"
          >
          <span
            class="flickon-atc-setup-drawer__choice-mark"
            aria-hidden="true"
          ></span>
          <span class="flickon-atc-setup-drawer__choice-title">
            Factory Ford
          </span>
        </label>

        <label
          class="flickon-atc-setup-drawer__choice"
          data-flickon-atc-setup-option="flickon"
        >
          <input
            type="radio"
            name="flickon-atc-setup"
            value="FlickOn"
          >
          <span
            class="flickon-atc-setup-drawer__choice-mark"
            aria-hidden="true"
          ></span>
          <span
            class="flickon-atc-setup-drawer__choice-title"
            data-flickon-atc-setup-label
          >
            FlickOn
          </span>
        </label>

        <label
          class="flickon-atc-setup-drawer__choice"
          data-flickon-atc-setup-option="not-sure"
        >
          <input
            type="radio"
            name="flickon-atc-setup"
            value="Not sure"
          >
          <span
            class="flickon-atc-setup-drawer__choice-mark"
            aria-hidden="true"
          ></span>
          <span class="flickon-atc-setup-drawer__choice-title">
            Not sure
          </span>
        </label>
      </div>

      <p
        class="flickon-atc-setup-drawer__hint"
        data-flickon-atc-setup-hint
        hidden
      >
        No worries — we can verify the correct setup before dispatch.
        You can continue shopping or complete your order as usual.
      </p>

      <button
        type="button"
        class="flickon-atc-setup-drawer__confirm"
        data-flickon-atc-setup-confirm
        disabled
      >
        Confirm & add to cart
      </button>
    </section>
  `;

  document.body.appendChild(drawer);

  const style = document.createElement('style');

  style.textContent = `
    .flickon-atc-setup-drawer {
      position: fixed;
      inset: 0;
      z-index: 100000;
      visibility: hidden;
      pointer-events: none;
    }

    .flickon-atc-setup-drawer.is-open {
      visibility: visible;
      pointer-events: auto;
    }

    .flickon-atc-setup-drawer__overlay {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      border: 0;
      background: rgba(0, 0, 0, 0.46);
      opacity: 0;
      cursor: pointer;
      transition: opacity 220ms ease;
    }

    .flickon-atc-setup-drawer.is-open
    .flickon-atc-setup-drawer__overlay {
      opacity: 1;
    }

    .flickon-atc-setup-drawer__panel {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      max-height: 80dvh;
      box-sizing: border-box;
      padding:
        12px
        20px
        calc(24px + env(safe-area-inset-bottom));
      overflow-y: auto;
      overscroll-behavior: contain;
      background: #111111;
      color: #ffffff;
      border-radius: 24px 24px 0 0;
      transform: translateY(105%);
      transition:
        transform 280ms
        cubic-bezier(0.22, 0.61, 0.36, 1);
    }

    .flickon-atc-setup-drawer.is-open
    .flickon-atc-setup-drawer__panel {
      transform: translateY(0);
    }

    .flickon-atc-setup-drawer__handle {
      width: 42px;
      height: 4px;
      margin: 0 auto 22px;
      border-radius: 999px;
      background: rgba(255,255,255,.35);
    }

    .flickon-atc-setup-drawer__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
    }

    .flickon-atc-setup-drawer__title {
      margin: 0;
      color: #ffffff;
      font-size: 27px;
      line-height: 1.08;
      font-weight: 600;
      letter-spacing: -.02em;
    }

    .flickon-atc-setup-drawer__close {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 1px solid rgba(255,255,255,.20);
      border-radius: 50%;
      background: rgba(255,255,255,.05);
      color: #ffffff;
      font-size: 25px;
      line-height: 1;
      cursor: pointer;
    }

    .flickon-atc-setup-drawer__copy {
      margin: 18px 0 20px;
      color: #a7adaf;
      font-size: 15px;
      line-height: 1.45;
    }

    .flickon-atc-setup-drawer__choices {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;
    }

    .flickon-atc-setup-drawer__choice {
      display: flex;
      align-items: center;
      gap: 14px;
      width: 100%;
      padding: 16px 18px;
      border: 1px solid #3b4348;
      border-radius: 17px;
      background: #181d20;
      color: #ffffff;
      cursor: pointer;
    }

    .flickon-atc-setup-drawer__choice input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    .flickon-atc-setup-drawer__choice.is-selected {
      border-color: #06c0c2;
      background: #152225;
    }

    .flickon-atc-setup-drawer__choice-mark {
      position: relative;
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
      border: 1.5px solid #5a6368;
      border-radius: 50%;
      background: transparent;
    }

    .flickon-atc-setup-drawer__choice.is-selected
    .flickon-atc-setup-drawer__choice-mark {
      border-color: #06c0c2;
      background: #06c0c2;
    }

    .flickon-atc-setup-drawer__choice.is-selected
    .flickon-atc-setup-drawer__choice-mark::after {
      content: '';
      position: absolute;
      left: 7px;
      top: 3px;
      width: 5px;
      height: 9px;
      border: solid #111111;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    .flickon-atc-setup-drawer__choice-title {
      font-size: 17px;
      line-height: 1.25;
      font-weight: 700;
    }

    .flickon-atc-setup-drawer__hint {
      margin: 14px 0 0;
      color: #8d9598;
      font-size: 13px;
      line-height: 1.5;
    }

    .flickon-atc-setup-drawer__confirm {
      width: 100%;
      margin-top: 20px;
      padding: 16px 18px;
      border: 0;
      border-radius: 14px;
      background: #ffffff;
      color: #111111;
      font-size: 15px;
      line-height: 1.2;
      font-weight: 700;
      cursor: pointer;
    }

    .flickon-atc-setup-drawer__confirm:disabled {
      opacity: 0.38;
      cursor: not-allowed;
    }

    @media screen and (min-width: 750px) {
      .flickon-atc-setup-drawer__panel {
        left: 50%;
        right: auto;
        width: min(560px, calc(100% - 40px));
        transform: translate(-50%, 105%);
        border-radius: 24px 24px 0 0;
      }

      .flickon-atc-setup-drawer.is-open
      .flickon-atc-setup-drawer__panel {
        transform: translate(-50%, 0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .flickon-atc-setup-drawer__overlay,
      .flickon-atc-setup-drawer__panel {
        transition: none;
      }
    }
  `;

  document.head.appendChild(style);

  drawer.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    if (event.target.name !== 'flickon-atc-setup') {
      return;
    }

    flickonSyncAtcSetupDrawerSelection(drawer);
  });

  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (event.target.closest('[data-flickon-atc-setup-close]')) {
      flickonCloseAtcSetupDrawer();
      return;
    }

    if (!event.target.closest('[data-flickon-atc-setup-confirm]')) {
      return;
    }

    const selected = drawer.querySelector(
      'input[name="flickon-atc-setup"]:checked'
    );

    if (!(selected instanceof HTMLInputElement)) {
      return;
    }

    const productForm = drawer.__flickonProductForm;

    if (!(productForm instanceof HTMLElement)) {
      return;
    }

    const productId = String(productForm.dataset.productId || '');
    const setupKind =
      drawer.dataset.setupKind ||
      flickonGetCurrentSetupKind(productId);

    flickonGetAtcSetupStore()[productId] = {
      setup: selected.value,
      setupKind,
      verify: selected.value === 'Not sure',
    };

    productForm.dataset.flickonAtcSetupReady = 'true';
    flickonApplyAtcSetupProperties(productForm);
    flickonCloseAtcSetupDrawer();

    const addToCartButton = productForm.querySelector(
      '[ref="addToCartButton"]'
    );

    if (addToCartButton instanceof HTMLElement) {
      delete addToCartButton.dataset.puppet;
      delete addToCartButton.dataset.flickonAtcDeferred;
      addToCartButton.click();
      return;
    }

    productForm.querySelector('form')?.requestSubmit?.();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    if (!drawer.classList.contains('is-open')) {
      return;
    }

    flickonCloseAtcSetupDrawer();
  });

  return drawer;
}

/**
 * Opens the installation setup drawer for the active product form.
 *
 * @param {HTMLElement | null | undefined} productForm
 */
function flickonOpenAtcSetupDrawer(productForm) {
  if (!flickonProductUsesAtcSetupConfirmation(productForm)) {
    return;
  }

  flickonCloseSetupDrawer();

  const drawer = flickonEnsureAtcSetupDrawer();

  drawer.__flickonProductForm = productForm;
  drawer.dataset.productId = String(
    productForm.dataset.productId || ''
  );

  flickonSyncAtcSetupDrawerOptions(
    drawer,
    productForm.dataset.productId
  );
  flickonResetAtcSetupDrawer(drawer);

  if (!drawer.classList.contains('is-open')) {
    drawer.dataset.previousHtmlOverflow =
      document.documentElement.style.overflow;

    drawer.dataset.previousBodyOverflow =
      document.body.style.overflow;
  }

  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');

  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
}


/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'pointerenter',
      this.#preloadImage
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */
        (timeoutId) =>
          clearTimeout(timeoutId)
      );
    }

    this.removeEventListener(
      'pointerenter',
      this.#preloadImage
    );
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const productForm =
      /** @type {ProductFormComponent | null} */
      (
        this.closest(
          'product-form-component'
        )
      );

    const productId =
      productForm?.dataset.productId;

    /*
      FLICKON VALIDATION

      Stop BEFORE animation and before Shopify
      tries to add the default Ice-Blue variant.

      This applies only to products containing
      Factory Ambient Lighting Setup.
    */

    if (
      flickonNeedsSetupConfirmation(
        productId
      )
    ) {
      event.preventDefault();
      event.stopPropagation();

      flickonMarkAtcDeferred(
        event,
        this.refs.addToCartButton
      );

      flickonOpenSetupDrawer(
        productId
      );

      return;
    }

    if (
      flickonNeedsAtcSetupConfirmation(
        productForm
      )
    ) {
      event.preventDefault();
      event.stopPropagation();

      flickonMarkAtcDeferred(
        event,
        this.refs.addToCartButton
      );

      flickonOpenAtcSetupDrawer(
        productForm
      );

      return;
    }

    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector =
      productForm?.refs.quantitySelector;

    if (
      quantitySelector?.canAddToCart
    ) {
      const validation =
        quantitySelector.canAddToCart();

      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    if (
      this.refs.addToCartButton
        .dataset.puppet !== 'true'
    ) {
      const animationEnabled =
        this.dataset.addToCartAnimation ===
        'true';

      if (
        animationEnabled &&
        !event.target.closest(
          '.quick-add-modal'
        )
      ) {
        this.#animateFlyToCart();
      }

      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image =
      this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } =
      this.refs;

    const cartIcon =
      document.querySelector(
        '.header-actions__cart-icon'
      );

    const image =
      this.dataset.productVariantMedia;

    if (
      !cartIcon ||
      !addToCartButton ||
      !image
    ) {
      return;
    }

    const flyToCartElement =
      /** @type {FlyToCart} */
      (
        document.createElement(
          'fly-to-cart'
        )
      );

    let flyToCartClass =
      addToCartButton.classList.contains(
        'quick-add__button'
      )
        ? 'fly-to-cart--quick'
        : 'fly-to-cart--main';

    flyToCartElement.classList.add(
      flyToCartClass
    );

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.style.setProperty(
      '--start-opacity',
      '0'
    );

    flyToCartElement.source =
      addToCartButton;

    flyToCartElement.destination =
      cartIcon;

    document.body.appendChild(
      flyToCartElement
    );
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } =
      this.refs;

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */
      (timeoutId) =>
        clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    if (
      addToCartButton.dataset.added !==
      'true'
    ) {
      addToCartButton.dataset.added =
        'true';
    }

    await yieldToMainThread();

    await onAnimationEnd(
      addToCartButton
    );

    const timeoutId =
      setTimeout(() => {
        addToCartButton.removeAttribute(
          'data-added'
        );

        const index =
          this.#resetTimeouts.indexOf(
            timeoutId
          );

        if (index > -1) {
          this.#resetTimeouts.splice(
            index,
            1
          );
        }
      }, 800);

    this.#resetTimeouts.push(
      timeoutId
    );
  };
}

if (
  !customElements.get(
    'add-to-cart-component'
  )
) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} quantityLabelCartCount
 * @property {HTMLElement | undefined} quantityRules
 * @property {HTMLElement | undefined} productFormButtons
 * @property {HTMLElement | undefined} volumePricing
 * @property {any | undefined} quantitySelector
 * @property {HTMLElement | undefined} quantitySelectorWrapper
 * @property {HTMLElement | undefined} quantityLabel
 * @property {HTMLElement | undefined} pricePerItem
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = [
    'variantId',
    'liveRegion',
  ];

  #abortController =
    new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress =
    false;

  /** @type {number} */
  #variantChangeGeneration =
    0;

  /**
   * @type {Array<{
   *   variantId: string,
   *   quantity: number
   * }>}
   */
  #addToCartQueue = [];

  /**
   * When installation setup is confirmed during a variant
   * change, finish via FormData instead of the property-less queue.
   * @type {boolean}
   */
  #pendingConfirmedAdd = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } =
      this.#abortController;

    const target =
      this.closest(
        '.shopify-section, dialog, product-card'
      );

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#onProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */
      (
        this.querySelector(
          'input[name="id"]'
        )
      )?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent =
      document.querySelector(
        'cart-items-component'
      );

    if (cartItemsComponent) {
      await customElements.whenDefined(
        'cart-items-component'
      );

      return cartItemsComponent.fetchCartData();
    }

    return fetch(
      `${Theme.routes.cart_url}.json`,
      {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      }
    ).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Failed to fetch cart: ${response.status}`
        );
      }

      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart
   */
  #updateCartQuantity(cart) {
    const variantIdInput =
      this.#getVariantIdInput();

    if (!variantIdInput) return;

    const cartItem =
      cart.items.find(
        /** @param {any} item */
        (item) =>
          item.variant_id.toString() ===
          variantIdInput.toString()
      );

    const cartQty =
      cartItem
        ? cartItem.quantity
        : 0;

    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */
      (
        this.querySelector(
          'quantity-selector-component'
        )
      );

    if (
      quantitySelector?.setCartQuantity
    ) {
      quantitySelector.setCartQuantity(
        cartQty
      );
    }

    this.#updateQuantityLabel(
      cartQty
    );
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) {
      return;
    }

    event.promise
      ?.then(({ detail }) => {
        if (
          detail?.sourceId === this.id ||
          detail?.source ===
            'product-form-component'
        ) {
          return;
        }

        if (detail?.items) {
          this.#updateCartQuantity(
            /** @type {Cart} */
            ({
              items: detail.items,
            })
          );
        } else {
          this.#refreshCart().then(
            (cart) =>
              this.#updateCartQuantity(
                cart
              )
          );
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[product-form] Event promise rejected:',
            error
          );
        }
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    /*
      SECOND FLICKON GUARD

      Even if submission is triggered by another
      UI element, keyboard, sticky bar or custom code,
      Unknown setup must not reach the Shopify cart.

      This makes the validation independent
      from the visible Add to Cart button.
    */

    const productId =
      this.dataset.productId;

    if (
      flickonNeedsSetupConfirmation(
        productId
      )
    ) {
      flickonOpenSetupDrawer(
        productId
      );

      return;
    }

    if (
      flickonNeedsAtcSetupConfirmation(
        this
      )
    ) {
      flickonOpenAtcSetupDrawer(
        this
      );

      return;
    }

    if (
      this.#variantChangeInProgress
    ) {
      if (
        flickonProductUsesAtcSetupConfirmation(
          this
        )
      ) {
        this.#pendingConfirmedAdd =
          true;

        return;
      }

      const intendedVariantId =
        this.#getIntendedVariantId();

      const quantity =
        this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({
          variantId:
            intendedVariantId,

          quantity:
            quantity,
        });
      }

      this.refs
        .addToCartButtonContainer
        ?.animateAddToCart?.();

      return;
    }

    this.#processAddToCart(
      undefined,
      undefined,
      event
    );
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return (
      new URL(
        window.location.href
      ).searchParams.get(
        'variant'
      ) ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return (
      Number(
        this.refs.quantitySelector
          ?.getValue?.()
      ) ||
      Number(
        this.dataset.quantityDefault
      ) ||
      1
    );
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(
    overrideVariantId,
    overrideQuantity,
    event
  ) {
    const {
      addToCartTextError,
    } = this.refs;

    if (this.#timeout) {
      clearTimeout(
        this.#timeout
      );
    }

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */
      (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    if (!overrideVariantId) {
      const anyButtonDisabled =
        Array.from(
          allAddToCartContainers
        ).some(
          (container) =>
            container.refs
              .addToCartButton
              ?.disabled
        );

      if (anyButtonDisabled) {
        return;
      }
    }

    const form =
      this.querySelector(
        'form'
      );

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }

    if (
      !overrideVariantId &&
      this.refs.quantitySelector
        ?.canAddToCart
    ) {
      const validation =
        this.refs.quantitySelector
          .canAddToCart();

      if (!validation.canAdd) {
        for (
          const container
          of allAddToCartContainers
        ) {
          container.disable();
        }

        const errorTemplate =
          this.dataset
            .quantityErrorMax || '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity
              ?.toString() || ''
          );

        if (addToCartTextError) {
          addToCartTextError.classList
            .remove('hidden');

          const textNode =
            addToCartTextError
              .childNodes[2];

          if (textNode) {
            textNode.textContent =
              errorMessage;
          } else {
            const newTextNode =
              document.createTextNode(
                errorMessage
              );

            addToCartTextError
              .appendChild(
                newTextNode
              );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          if (this.#timeout) {
            clearTimeout(
              this.#timeout
            );
          }

          this.#timeout =
            setTimeout(() => {
              if (
                !addToCartTextError
              ) {
                return;
              }

              addToCartTextError
                .classList
                .add('hidden');

              this.#clearLiveRegionText();
            },
            ERROR_MESSAGE_DISPLAY_DURATION
          );
        }

        setTimeout(() => {
          for (
            const container
            of allAddToCartContainers
          ) {
            container.enable();
          }
        },
        ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    flickonApplyAtcSetupProperties(this);

    const formData =
      new FormData(form);

    if (overrideVariantId) {
      formData.set(
        'id',
        overrideVariantId
      );
    }

    if (
      overrideQuantity !== undefined
    ) {
      formData.set(
        'quantity',
        overrideQuantity.toString()
      );
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    let cartItemComponentsSectionIds =
      [];

    cartItemsComponents.forEach(
      (item) => {
        if (
          item instanceof HTMLElement &&
          item.dataset.sectionId
        ) {
          cartItemComponentsSectionIds
            .push(
              item.dataset.sectionId
            );
        }

        formData.append(
          'sections',
          cartItemComponentsSectionIds
            .join(',')
        );
      }
    );

    const itemCount =
      Number(
        formData.get(
          'quantity'
        )
      ) ||
      Number(
        this.dataset.quantityDefault
      );

    const deferredEventPromise =
      CartLinesUpdateEvent
        .createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',

        context: 'product',

        lines: [
          {
            merchandiseId:
              /** @type {string} */
              (
                formData.get('id')
              ),

            quantity:
              itemCount,
          },
        ],

        promise:
          deferredEventPromise
            .promise,
      })
    );

    const fetchCfg =
      fetchConfig(
        'javascript',
        {
          body: formData,
        }
      );

    fetch(
      Theme.routes.cart_add_url,
      {
        ...fetchCfg,

        headers: {
          ...fetchCfg.headers,

          Accept:
            'text/html',
        },
      }
    )
      .then(
        (response) =>
          response.json()
      )

      .then(
        async (response) => {
          if (response.status) {
            this.dispatchEvent(
              new CartErrorEvent({
                error:
                  response.message ||
                  'Add to cart failed',

                code:
                  'INVALID',

                detail: {
                  description:
                    response.description,

                  errors:
                    response.errors,
                },
              })
            );

            this.#refreshCart()
              .then(
                (ajaxCart) =>
                  deferredEventPromise
                    .resolve({
                      cart:
                        CartLinesUpdateEvent
                          .createCartFromAjaxResponse(
                            ajaxCart
                          ),

                      detail: {
                        didError: true,

                        items:
                          ajaxCart.items,

                        source:
                          'product-form-component',

                        sourceId:
                          this.id.toString(),

                        itemCount,

                        productId:
                          this.dataset
                            .productId,
                      },
                    })
              )
              .catch(
                deferredEventPromise
                  .reject
              );

            if (
              !addToCartTextError
            ) {
              return;
            }

            addToCartTextError
              .classList
              .remove('hidden');

            const textNode =
              addToCartTextError
                .childNodes[2];

            if (textNode) {
              textNode.textContent =
                response.message;
            } else {
              const newTextNode =
                document.createTextNode(
                  response.message
                );

              addToCartTextError
                .appendChild(
                  newTextNode
                );
            }

            this.#setLiveRegionText(
              response.message
            );

            this.#timeout =
              setTimeout(() => {
                if (
                  !addToCartTextError
                ) {
                  return;
                }

                addToCartTextError
                  .classList
                  .add('hidden');

                this
                  .#clearLiveRegionText();
              },
              ERROR_MESSAGE_DISPLAY_DURATION);

            return;

          } else {

            const id =
              formData.get('id');

            if (
              addToCartTextError
            ) {
              addToCartTextError
                .classList
                .add('hidden');

              addToCartTextError
                .removeAttribute(
                  'aria-live'
                );
            }

            if (!id) {
              throw new Error(
                'Form ID is required'
              );
            }

            const anyAddToCartButton =
              allAddToCartContainers[
                0
              ]?.refs
                .addToCartButton;

            if (
              anyAddToCartButton
            ) {
              const addedTextElement =
                anyAddToCartButton
                  .querySelector(
                    '.add-to-cart-text--added'
                  );

              const addedText =
                addedTextElement
                  ?.textContent
                  ?.trim() ||
                Theme.translations
                  .added;

              this.#setLiveRegionText(
                addedText
              );

              setTimeout(
                () => {
                  this
                    .#clearLiveRegionText();
                },
                SUCCESS_MESSAGE_DISPLAY_DURATION
              );
            }

            const cart =
              await this
                .#refreshCart()
                .then(
                  (ajaxCart) => {
                    deferredEventPromise
                      .resolve({
                        cart:
                          CartLinesUpdateEvent
                            .createCartFromAjaxResponse(
                              ajaxCart
                            ),

                        detail: {
                          items:
                            ajaxCart.items,

                          source:
                            'product-form-component',

                          sourceId:
                            this.id
                              .toString(),

                          itemCount,

                          productId:
                            this.dataset
                              .productId,

                          sections:
                            response.sections,

                          didError:
                            false,
                        },
                      });

                    if (
                      this
                        .#getVariantIdInput()
                    ) {
                      this
                        .#updateCartQuantity(
                          ajaxCart
                        );
                    }

                    return ajaxCart;
                  }
                )
                .catch(
                  deferredEventPromise
                    .reject
                );
          }
        }
      )

      .catch(
        (error) => {
          console.error(
            error
          );

          deferredEventPromise
            .reject(error);

          this.dispatchEvent(
            new CartErrorEvent({
              error:
                error?.message ||
                'Network error during add to cart',

              code:
                'SERVICE_UNAVAILABLE',
            })
          );
        }
      )

      .finally(() => {
        flickonClearAtcSetupReady(this);

        if (event) {
          cartPerformance
            .measureFromEvent(
              'add:user-action',
              event
            );
        }
      });
  }

  /**
   * @param {Array<{
   *   variantId: string,
   *   quantity: number
   * }>} items
   */
  #processBatchAddToCart(items) {
    if (items.length === 0) {
      return;
    }

    /*
      Also guard queued / sticky requests.
    */

    if (
      flickonNeedsSetupConfirmation(
        this.dataset.productId
      )
    ) {
      flickonOpenSetupDrawer(
        this.dataset.productId
      );

      return;
    }

    if (
      flickonNeedsAtcSetupConfirmation(
        this
      )
    ) {
      flickonOpenAtcSetupDrawer(
        this
      );

      return;
    }

    const {
      addToCartTextError,
    } = this.refs;

    if (this.#timeout) {
      clearTimeout(
        this.#timeout
      );
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds =
      [];

    for (
      const item
      of cartItemsComponents
    ) {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds
          .push(
            item.dataset.sectionId
          );
      }
    }

    const totalQuantity =
      items.reduce(
        (sum, item) =>
          sum + item.quantity,
        0
      );

    const deferredEventPromise =
      CartLinesUpdateEvent
        .createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',

        context: 'product',

        lines:
          items.map(
            (item) => ({
              merchandiseId:
                item.variantId,

              quantity:
                item.quantity,
            })
          ),

        promise:
          deferredEventPromise
            .promise,
      })
    );

    const atcProperties =
      flickonGetAtcSetupPropertyMap(
        this
      );

    const payload = {
      items:
        items.map(
          (item) => {
            const line = {
              id:
                Number(
                  item.variantId
                ),

              quantity:
                item.quantity,
            };

            if (atcProperties) {
              line.properties =
                atcProperties;
            }

            return line;
          }
        ),

      sections:
        cartItemComponentsSectionIds
          .join(','),
    };

    fetch(
      Theme.routes.cart_add_url,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json',
        },

        body:
          JSON.stringify(
            payload
          ),
      }
    )
      .then(
        (response) =>
          response.json()
      )

      .then(
        async (response) => {
          if (response.status) {
            this.dispatchEvent(
              new CartErrorEvent({
                error:
                  response.message ||
                  'Add to cart failed',

                code:
                  'INVALID',

                detail: {
                  description:
                    response.description,

                  errors:
                    response.errors,
                },
              })
            );

            this.#refreshCart()
              .then(
                (ajaxCart) =>
                  deferredEventPromise
                    .resolve({
                      cart:
                        CartLinesUpdateEvent
                          .createCartFromAjaxResponse(
                            ajaxCart
                          ),

                      detail: {
                        didError:
                          true,

                        items:
                          ajaxCart.items,

                        source:
                          'product-form-component',

                        sourceId:
                          this.id
                            .toString(),

                        itemCount:
                          totalQuantity,

                        productId:
                          this.dataset
                            .productId,
                      },
                    })
              )
              .catch(
                deferredEventPromise
                  .reject
              );

            if (
              !addToCartTextError
            ) {
              return;
            }

            addToCartTextError
              .classList
              .remove('hidden');

            const textNode =
              addToCartTextError
                .childNodes[2];

            if (textNode) {
              textNode.textContent =
                response.message;
            } else {
              addToCartTextError
                .appendChild(
                  document
                    .createTextNode(
                      response.message
                    )
                );
            }

            this.#setLiveRegionText(
              response.message
            );

            this.#timeout =
              setTimeout(
                () => {
                  addToCartTextError
                    .classList
                    .add('hidden');

                  this
                    .#clearLiveRegionText();
                },
                ERROR_MESSAGE_DISPLAY_DURATION
              );

            return;
          }

          if (
            addToCartTextError
          ) {
            addToCartTextError
              .classList
              .add('hidden');

            addToCartTextError
              .removeAttribute(
                'aria-live'
              );
          }

          const allAddToCartContainers =
            /** @type {NodeListOf<AddToCartComponent>} */
            (
              this.querySelectorAll(
                'add-to-cart-component'
              )
            );

          const anyAddToCartButton =
            allAddToCartContainers[
              0
            ]?.refs
              .addToCartButton;

          if (
            anyAddToCartButton
          ) {
            const addedTextElement =
              anyAddToCartButton
                .querySelector(
                  '.add-to-cart-text--added'
                );

            const addedText =
              addedTextElement
                ?.textContent
                ?.trim() ||
              Theme.translations
                .added;

            this.#setLiveRegionText(
              addedText
            );

            setTimeout(
              () =>
                this
                  .#clearLiveRegionText(),
              SUCCESS_MESSAGE_DISPLAY_DURATION
            );
          }

          const cart =
            await this
              .#refreshCart();

          deferredEventPromise
            .resolve({
              cart:
                CartLinesUpdateEvent
                  .createCartFromAjaxResponse(
                    cart
                  ),

              detail: {
                items:
                  cart.items,

                source:
                  'product-form-component',

                sourceId:
                  this.id
                    .toString(),

                itemCount:
                  totalQuantity,

                productId:
                  this.dataset
                    .productId,

                sections:
                  response.sections,

                didError:
                  false,
              },
            });

          this
            .#updateCartQuantity(
              cart
            );
        }
      )

      .catch(
        (error) => {
          console.error(
            error
          );

          deferredEventPromise
            .reject(
              error
            );

          this.dispatchEvent(
            new CartErrorEvent({
              error:
                error?.message ||
                'Network error during add to cart',

              code:
                'SERVICE_UNAVAILABLE',
            })
          );
        }
      )

      .finally(() => {
        flickonClearAtcSetupReady(this);
      });
  }

  /**
   * Updates quantity label with current cart quantity.
   * @param {number} cartQty
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs
        .quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel
          .textContent
          ?.match(
            /\((\d+)\s+(.+)\)/
          );

      if (
        inCartText &&
        inCartText[2]
      ) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      text;
  }

  #clearLiveRegionText() {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent =
      '';
  }

  /**
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );

    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();

    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement
        .insertAdjacentElement(
          'beforebegin',
          /** @type {Element} */
          (
            newElement.cloneNode(
              true
            )
          )
        );
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect =
    async (event) => {
      const sourceCard =
        /** @type {Element | null} */
        (event.target)
          ?.closest(
            'product-card'
          );

      if (
        sourceCard &&
        !sourceCard.contains(this)
      ) {
        return;
      }

      const generation =
        ++this
          .#variantChangeGeneration;

      this.#variantChangeInProgress =
        true;

      try {
        const { detail } =
          await event.promise;

        if (!detail?.html) {
          return;
        }

        const {
          html,
          newProduct,
          productId,
          resource,
        } = detail;

        if (newProduct) {
          this.dataset.productId =
            newProduct.id;

        } else if (
          productId &&
          productId !==
            this.dataset.productId
        ) {
          return;
        }

        const { variantId } =
          this.refs;

        variantId.value =
          resource?.id ?? '';

        const {
          addToCartButtonContainer:
            currentAddToCartButtonContainer,

          acceleratedCheckoutButtonContainer,
        } = this.refs;

        const currentAddToCartButton =
          currentAddToCartButtonContainer
            ?.refs
            .addToCartButton;

        if (
          !currentAddToCartButtonContainer ||
          (
            !currentAddToCartButton &&
            !acceleratedCheckoutButtonContainer
          )
        ) {
          return;
        }

        if (
          resource == null ||
          resource.available == false
        ) {
          currentAddToCartButtonContainer
            .disable();
        } else {
          currentAddToCartButtonContainer
            .enable();
        }

        const newAddToCartButton =
          html.querySelector(
            'product-form-component [ref="addToCartButton"]'
          );

        if (
          newAddToCartButton &&
          currentAddToCartButton
        ) {
          morph(
            currentAddToCartButton,
            newAddToCartButton
          );
        }

        if (
          acceleratedCheckoutButtonContainer
        ) {
          if (
            resource == null ||
            resource.available == false
          ) {
            acceleratedCheckoutButtonContainer
              ?.setAttribute(
                'hidden',
                'true'
              );
          } else {
            acceleratedCheckoutButtonContainer
              ?.removeAttribute(
                'hidden'
              );
          }
        }

        if (resource) {
          const productVariantMedia =
            resource.featured_media
              ?.preview_image
              ?.src;

          if (
            productVariantMedia
          ) {
            this.refs
              .addToCartButtonContainer
              ?.setAttribute(
                'data-product-variant-media',
                productVariantMedia +
                  '&width=100'
              );
          }
        }

        const {
          quantityRules,
          pricePerItem,
          quantitySelector,
          productFormButtons,
          quantityLabel,
          quantitySelectorWrapper,
        } = this.refs;

        const newQuantityInput =
          /** @type {HTMLInputElement | null} */
          (
            html.querySelector(
              'quantity-selector-component input[ref="quantityInput"]'
            )
          );

        if (
          quantitySelector
            ?.updateConstraints &&
          newQuantityInput
        ) {
          quantitySelector
            .updateConstraints(
              newQuantityInput.min,
              newQuantityInput.max ||
                null,
              newQuantityInput.step
            );

          this.dataset
            .quantityDefault =
              newQuantityInput.min ||
              '1';
        }

        const newQuantityRules =
          html.querySelector(
            '.quantity-rules'
          );

        const isQuantityRulesChanging =
          !!quantityRules !==
          !!newQuantityRules;

        const newPricePerItem =
          html.querySelector(
            'price-per-item'
          );

        const isPricePerItemChanging =
          !!pricePerItem !==
          !!newPricePerItem;

        if (
          (
            isQuantityRulesChanging ||
            isPricePerItemChanging
          ) &&
          quantitySelector
        ) {
          const currentQuantityValue =
            quantitySelector
              .getValue?.();

          const newProductFormButtons =
            html.querySelector(
              '.product-form-buttons'
            );

          if (
            productFormButtons &&
            newProductFormButtons
          ) {
            morph(
              productFormButtons,
              newProductFormButtons
            );

            const newQuantityInputElement =
              /** @type {HTMLInputElement | null} */
              (
                html.querySelector(
                  'quantity-selector-component input[ref="quantityInput"]'
                )
              );

            if (
              this.refs
                .quantitySelector
                ?.updateConstraints &&
              newQuantityInputElement &&
              currentQuantityValue
            ) {
              this.refs
                .quantitySelector
                .setValue(
                  currentQuantityValue
                );

              this.refs
                .quantitySelector
                .updateConstraints(
                  newQuantityInputElement
                    .min,

                  newQuantityInputElement
                    .max ||
                    null,

                  newQuantityInputElement
                    .step
                );

              this.dataset
                .quantityDefault =
                  newQuantityInputElement
                    .min ||
                  '1';
            }
          }

        } else {

          const morphTargets =
            /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
            ([
              [
                '.quantity-label',
                quantityLabel,
                quantitySelector,
              ],
              [
                '.quantity-rules',
                quantityRules,
                this.refs
                  .productFormButtons,
              ],
              [
                'price-per-item',
                pricePerItem,
                quantitySelectorWrapper,
              ],
            ]);

          for (
            const [
              selector,
              currentElement,
              fallback,
            ]
            of morphTargets
          ) {
            this
              .#morphOrUpdateElement(
                currentElement,
                html.querySelector(
                  selector
                ),
                fallback
              );
          }
        }

        const currentVolumePricing =
          this.refs.volumePricing;

        const newVolumePricing =
          html.querySelector(
            'volume-pricing'
          );

        this
          .#morphOrUpdateElement(
            currentVolumePricing,
            newVolumePricing,
            this.refs
              .productFormButtons
          );

        const hasB2BFeatures =
          quantityRules ||
          newQuantityRules ||
          pricePerItem ||
          newPricePerItem ||
          currentVolumePricing ||
          newVolumePricing;

        if (!hasB2BFeatures) {
          return;
        }

        this.#refreshCart()
          .then(
            (cart) =>
              this
                .#updateCartQuantity(
                  cart
                )
          );

      } finally {

        if (
          generation ===
          this
            .#variantChangeGeneration
        ) {
          this.#variantChangeInProgress =
            false;

          if (this.#pendingConfirmedAdd) {
            this.#pendingConfirmedAdd =
              false;

            this.#processAddToCart();
            return;
          }

          if (
            this.#addToCartQueue
              .length > 0
          ) {
            const queuedItems =
              [
                ...this
                  .#addToCartQueue,
              ];

            this.#addToCartQueue =
              [];

            this
              .#processBatchAddToCart(
                queuedItems
              );
          }
        }
      }
    };
}

if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}